import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/services/api";
import {
  ChevronDown,
  ChevronUp,
  FileText,
  Send,
  CheckCircle,
  XCircle,
  Eye,
  ShieldCheck,
  ShieldOff,
  Hash,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface AuditLogEntry {
  id: number;
  action: string;
  user: { id: number; name: string; email: string } | null;
  details: Record<string, any> | null;
  ip_address: string | null;
  created_at: string;
}

interface ConsentRecord {
  signer: string;
  email: string | null;
  consent_given_at: string | null;
  consent_ip_address: string | null;
  consent_withdrawn_at: string | null;
  document_hash_at_signing: string | null;
}

interface AuditTrailPanelProps {
  documentId: string;
  className?: string;
}

const ACTION_ICONS: Record<string, React.ReactNode> = {
  document_created: <FileText className="h-3.5 w-3.5 text-blue-500" />,
  document_sent: <Send className="h-3.5 w-3.5 text-indigo-500" />,
  document_signed: <CheckCircle className="h-3.5 w-3.5 text-green-500" />,
  document_declined: <XCircle className="h-3.5 w-3.5 text-red-500" />,
  document_viewed: <Eye className="h-3.5 w-3.5 text-gray-500" />,
  document_updated: <FileText className="h-3.5 w-3.5 text-amber-500" />,
  document_deleted: <XCircle className="h-3.5 w-3.5 text-red-400" />,
  document_shared: <Send className="h-3.5 w-3.5 text-purple-500" />,
  esign_consent_given: <ShieldCheck className="h-3.5 w-3.5 text-green-600" />,
  esign_consent_withdrawn: <ShieldOff className="h-3.5 w-3.5 text-orange-500" />,
  document_hash_verified: <Hash className="h-3.5 w-3.5 text-cyan-500" />,
  signing_requests_created: <Send className="h-3.5 w-3.5 text-indigo-500" />,
};

const AuditTrailPanel: React.FC<AuditTrailPanelProps> = ({
  documentId,
  className,
}) => {
  const { t } = useTranslation(["documents", "common"]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [consentRecords, setConsentRecords] = useState<ConsentRecord[]>([]);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (isOpen && !hasFetched) {
      fetchAuditTrail();
    }
  }, [isOpen]);

  const fetchAuditTrail = async () => {
    setIsLoading(true);
    try {
      const response = (await api.get(
        `/documents/${documentId}/audit-trail`
      )) as any;
      setAuditLogs(response.audit_logs || []);
      setConsentRecords(response.consent_records || []);
      setHasFetched(true);
    } catch (err) {
      console.error("Failed to fetch audit trail:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatAction = (action: string) => {
    const key = `documents:audit_action_${action}`;
    const translated = t(key);
    // If no translation, humanize the action name
    if (translated === key) {
      return action
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }
    return translated;
  };

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleString();
  };

  return (
    <div className={cn("border-t border-border", className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/40 transition-colors"
      >
        <h3 className="text-sm font-medium">{t("documents:audit_trail_title")}</h3>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {isOpen && (
        <div className="px-4 pb-4 max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Audit log timeline */}
              {auditLogs.length > 0 ? (
                <div className="relative">
                  <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
                  <div className="space-y-3">
                    {auditLogs.map((log) => (
                      <div key={log.id} className="flex gap-3 relative">
                        <div className="mt-1 z-10 bg-card rounded-full p-0.5">
                          {ACTION_ICONS[log.action] || (
                            <FileText className="h-3.5 w-3.5 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium">
                            {formatAction(log.action)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {log.user?.name || t("documents:audit_system")}
                            {log.ip_address && (
                              <span className="ml-1">· {log.ip_address}</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground/70">
                            {formatDate(log.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {t("documents:audit_no_events")}
                </p>
              )}

              {/* Consent records */}
              {consentRecords.length > 0 && (
                <div className="mt-4 pt-3 border-t border-border">
                  <h4 className="text-xs font-medium text-muted-foreground mb-2">
                    {t("documents:audit_consent_records")}
                  </h4>
                  <div className="space-y-2">
                    {consentRecords.map((record, idx) => (
                      <div
                        key={idx}
                        className="bg-muted/40 rounded-md p-2 text-xs"
                      >
                        <p className="font-medium">{record.signer}</p>
                        <p className="text-muted-foreground">
                          {record.consent_given_at &&
                            `${t("documents:audit_consent_at")}: ${formatDate(record.consent_given_at)}`}
                        </p>
                        {record.consent_withdrawn_at && (
                          <p className="text-orange-600 font-medium">
                            {t("documents:audit_consent_withdrawn_at")}:{" "}
                            {formatDate(record.consent_withdrawn_at)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditTrailPanel;
